import { DraftPolicy, ReleaseNotes } from 'views/App/Portal/Policy/_types/manuals/draft';
import { baseApiUrl, fetcher, handleError, ServerErrorResponse } from 'data_sources/constants';

type NewPolicyRequest = {
  title: string;
  chapterId: string;
};

export default function useManagePolicy() {
  const create = async (
    manualId: string,
    data: NewPolicyRequest,
    callback?: (data: DraftPolicy) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/policies/v1/manuals/${manualId}/policies`;
    await fetcher({
      url,
      method: 'post',
      body: data,
    })
      .then((data) => {
        if (callback) callback(data);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const update = async (
    manualId: string,
    policyId: string,
    data: DraftPolicy,
    callback?: (data: DraftPolicy) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/policies/v1/manuals/${manualId}/policies/${policyId}`;
    await fetcher({
      url,
      method: 'put',
      body: data,
    })
      .then((data) => {
        if (callback) callback(data);
      })
      .catch((err) => {
        console.warn('update err', err);
        if (onError) handleError(err, { onError });
      });
  };

  const updateDraftStatus = async (
    manualId: string,
    policyId: string,
    draftState: string,
    callback?: () => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/policies/v1/manuals/${manualId}/policies/${policyId}/draft-state`;
    await fetcher({
      url,
      method: 'patch',
      body: { draftState },
    })
      .then(async () => {
        if (callback) await callback();
      })
      .catch((err) => {
        console.warn('update err', err);
        if (onError) handleError(err, { onError });
      });
  };

  const approveChanges = async (
    manualId: string,
    policyId: string,
    callback?: () => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/policies/v1/manuals/${manualId}/policies/${policyId}/approve-change`;
    await fetcher({
      url,
      method: 'post',
      body: {},
    })
      .then(async () => {
        if (callback) await callback();
      })
      .catch((err) => {
        console.warn('update err', err);
        if (onError) handleError(err, { onError });
      });
  };

  const getLock = async (
    manualId: string,
    policyId: string,
    callback?: (lockInfo: LockInfo) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/policies/v1/manuals/${manualId}/policies/${policyId}/lock`;
    await fetcher({
      url,
      method: 'post',
      body: {},
    })
      .then((data) => {
        if (callback) callback(data);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const takeLock = async (
    manualId: string,
    policyId: string,
    callback?: (lockInfo: LockInfo) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/policies/v1/manuals/${manualId}/policies/${policyId}/lock`;
    await fetcher({
      url,
      method: 'patch',
      body: {},
    })
      .then((data) => {
        if (callback) callback(data);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const removeLock = async (
    manualId: string,
    policyId: string,
    callback?: (lockInfo: LockInfo) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/policies/v1/manuals/${manualId}/policies/${policyId}/lock`;
    await fetcher({
      url,
      method: 'delete',
      body: {},
    })
      .then((data) => {
        if (callback) callback(data);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const updateGuideSheet = async (
    manualId: string,
    policyId: string,
    guideSheet: {
      title: string;
      content: string;
      contentEditable: string;
    },
    onSuccess?: (data: any) => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/policies/v1/manuals/${manualId}/policies/${policyId}/guide-sheet`;
    await fetcher({
      url,
      method: 'put',
      body: guideSheet,
    })
      .then((data) => {
        if (onSuccess) onSuccess(data);
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const approveGuideSheet = async (
    manualId: string,
    policyId: string,
    onSuccess?: () => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/policies/v1/manuals/${manualId}/policies/${policyId}/guide-sheet/approve-change`;
    await fetcher({
      url,
      method: 'post',
      body: {},
    })
      .then(() => {
        if (onSuccess) onSuccess();
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const updateReleaseNotes = async (
    manualId: string,
    policyId: string,
    body: { history: ReleaseNotes['history'] },
    onSuccess?: () => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/policies/v1/manuals/${manualId}/policies/${policyId}/release-notes`;
    await fetcher({
      url,
      method: 'put',
      body,
    })
      .then(() => {
        if (onSuccess) onSuccess();
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const approveReleaseNotes = async (
    manualId: string,
    policyId: string,
    onSuccess?: () => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/policies/v1/manuals/${manualId}/policies/${policyId}/release-notes/approve-change`;
    await fetcher({
      url,
      method: 'post',
    })
      .then(() => {
        if (onSuccess) onSuccess();
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  const recreateChangeSets = async (
    manualId: string,
    policyId: string,
    data: {
      content: Array<{ id: string; title: string }>;
      contentTitle: string;
      includeGuideSheet?: boolean;
      includeReleaseNotes?: boolean;
    },
    onSuccess?: () => void,
    onError?: (errors: ServerErrorResponse) => void
  ) => {
    const url = `${baseApiUrl()}/lexiverse/policies/v1/manuals/${manualId}/policies/${policyId}/recreate-change-sets`;
    await fetcher({
      url,
      method: 'post',
      body: data,
    })
      .then(() => {
        if (onSuccess) onSuccess();
      })
      .catch((err) => {
        if (onError) handleError(err, { onError });
      });
  };

  return {
    approveChanges,
    approveReleaseNotes,
    create,
    getLock,
    removeLock,
    takeLock,
    update,
    updateDraftStatus,
    updateGuideSheet,
    updateReleaseNotes,
    approveGuideSheet,
    recreateChangeSets,
  };
}
